import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString
} from 'libphonenumber-js'
import examples from 'libphonenumber-js/mobile/examples'

import { phoneConfig } from '~/config/phoneConfig'
import type { CountryCode } from '~/enums/CountryCode'
import { PhoneVerdict } from '~/enums/PhoneVerdict'
import { PhoneMessageKey } from '~/utils/PhoneMessageKey'
import type { ParsedPhoneNumber } from '~/interfaces/ParsedPhoneNumber'
import type { ApiFieldIssue } from '~/services/api/ApiFieldIssue'

const DEFAULT_COUNTRY: CountryCode = phoneConfig.allowedCountries[0]
const ALLOWED_CHARACTERS = /^[\d\s().+-]*$/

const callingCodeOf = (country: CountryCode): string => getCountryCallingCode(country)

const nationalDigitCounts = new Map<CountryCode, number>()

// The national number length of an enabled country, from its example number.
function nationalDigitsOf(country: CountryCode): number {
  const known = nationalDigitCounts.get(country)
  if (known !== undefined) return known
  const example = getExampleNumber(country, examples)
  if (example === undefined) throw new Error(`No example phone number for country ${country}`)
  nationalDigitCounts.set(country, example.nationalNumber.length)
  return example.nationalNumber.length
}

// Compatibility forms (fullwidth digits, typographic dashes), invisible and combining marks, and
// other punctuation or symbols that contacts apps and web pages add around numbers fold to plain
// phone punctuation; only letters and non-ASCII numerals stay rejected.
const foldPunctuation = (raw: string): string =>
  raw
    .normalize('NFKC')
    .replace(/[\p{Cf}\p{M}]/gu, '')
    .replace(/[\p{Pd}\u2212]/gu, '-')
    .replace(/[^\p{L}\p{N}\s().+-]/gu, ' ')

const carriesCallingCode = (digits: string, country: CountryCode): boolean =>
  digits.length === callingCodeOf(country).length + nationalDigitsOf(country) &&
  digits.startsWith(callingCodeOf(country))

const countryOfPlusNumber = (digits: string, selected: CountryCode): CountryCode | undefined =>
  [selected, ...phoneConfig.allowedCountries].find((country) =>
    digits.startsWith(callingCodeOf(country))
  )

const countryOfLongNumber = (digits: string, selected: CountryCode): CountryCode | undefined =>
  [selected, ...phoneConfig.allowedCountries].find((country) => carriesCallingCode(digits, country))

function verdictOf(national: string, country: CountryCode): PhoneVerdict {
  const required = nationalDigitsOf(country)
  if (national.length === 0) return PhoneVerdict.EMPTY
  if (national.length < required) return PhoneVerdict.INCOMPLETE
  // The default country only applies to numbers without a country code; a number in a
  // shared calling code (e.g. a Bahamas 242 number under +1) parses to its own country.
  const parsed = parsePhoneNumberFromString(national, country)
  if (national.length === required && parsed?.isValid() === true && parsed.country === country) {
    return PhoneVerdict.VALID
  }
  return PhoneVerdict.INVALID_NUMBER
}

function hasInvalidCharacters(raw: string): boolean {
  return !ALLOWED_CHARACTERS.test(foldPunctuation(raw))
}

interface PhoneSplit {
  readonly country: CountryCode
  readonly national: string
  readonly supported: boolean
  readonly pending: boolean
  readonly typedCode: string
}

function split(raw: string, selected: CountryCode): PhoneSplit {
  const folded = foldPunctuation(raw).trim()
  const digits = folded.replace(/\D/g, '')
  if (folded.startsWith('+')) {
    const country = countryOfPlusNumber(digits, selected)
    if (country === undefined && digits.length > 0) {
      return {
        country: selected,
        national: digits,
        pending: false,
        supported: false,
        typedCode: ''
      }
    }
    const national = country === undefined ? '' : digits.slice(callingCodeOf(country).length)
    return {
      country: country ?? selected,
      national,
      pending: national === '',
      supported: true,
      typedCode: digits
    }
  }

  const country = countryOfLongNumber(digits, selected) ?? selected
  const national = carriesCallingCode(digits, country)
    ? digits.slice(callingCodeOf(country).length)
    : digits
  return { country, national, pending: false, supported: true, typedCode: '' }
}

/** Splits an entry into country and national digits; an explicit country code selects that country, otherwise the selected one is implied. */
function parse(
  raw: string | null | undefined,
  selected: CountryCode = DEFAULT_COUNTRY
): ParsedPhoneNumber {
  const text = raw ?? ''
  if (hasInvalidCharacters(text)) {
    return { country: selected, national: '', verdict: PhoneVerdict.INVALID_CHARACTERS }
  }
  const { country, national, pending, supported } = split(text, selected)
  if (pending) return { country, national, verdict: PhoneVerdict.INCOMPLETE }
  const verdict = supported ? verdictOf(national, country) : PhoneVerdict.INVALID_NUMBER
  return { country, national, verdict }
}

/** The text a phone field shows for an entry: national format for an enabled country, `+digits` for any other; excess digits are shown unformatted, never dropped. */
function display(raw: string | null | undefined, selected: CountryCode = DEFAULT_COUNTRY): string {
  const text = raw ?? ''
  if (hasInvalidCharacters(text)) return ''
  const { country, national, pending, supported, typedCode } = split(text, selected)
  if (pending) return `+${typedCode}`
  if (supported) {
    return national.length <= nationalDigitsOf(country)
      ? new AsYouType(country).input(national)
      : national
  }
  return `+${national}`
}

/**
 * The national digits sent to the API for an entry. An entry without national digits (rejected
 * characters, a lone country code) passes through trimmed so validation still sees it; only a blank
 * entry yields an empty string.
 */
function wire(raw: string | null | undefined, selected: CountryCode = DEFAULT_COUNTRY): string {
  const { national } = parse(raw, selected)
  return national === '' ? (raw ?? '').trim() : national
}

/** A privacy-preserving hint of a number, e.g. `(410) ***-**38`; empty when too few digits are present. */
function mask(raw: string | null | undefined): string {
  const national = parse(raw).national
  if (national.length < 4) return ''
  return `(${national.slice(0, 3)}) ***-**${national.slice(-2)}`
}

/** Params a contract attaches to a custom zod issue so its message resolves from the verdict. */
function ruleParams(verdict: PhoneVerdict): { readonly phone_rule: PhoneVerdict } {
  return { phone_rule: verdict }
}

/** The verdict a custom zod issue was raised with via `ruleParams`; undefined for any other issue. */
function ruleVerdict(params: unknown): PhoneVerdict | undefined {
  if (typeof params === 'object' && params !== null && 'phone_rule' in params) {
    return Object.values(PhoneVerdict).find((candidate) => candidate === params.phone_rule)
  }
  return undefined
}

/** How many digits an entry holds, counting compatibility digits (e.g. fullwidth) like every other rule here. */
function digitCount(raw: string): number {
  return foldPunctuation(raw).replace(/\D/gu, '').length
}

// Only codes and params the closed field-error contract allows: a verdict that has no code of its own
// (rejected characters) presents as the invalid-number format failure.
const FIELD_ISSUE_BY_VERDICT: ReadonlyMap<
  PhoneVerdict,
  () => Pick<ApiFieldIssue, 'code' | 'params'>
> = new Map<PhoneVerdict, () => Pick<ApiFieldIssue, 'code' | 'params'>>([
  [PhoneVerdict.EMPTY, () => ({ code: 'FIELD_REQUIRED' })],
  [
    PhoneVerdict.INCOMPLETE,
    () => ({ code: 'FIELD_TOO_SHORT', params: { min: nationalDigitsOf(DEFAULT_COUNTRY) } })
  ],
  [
    PhoneVerdict.INVALID_CHARACTERS,
    () => ({ code: 'FIELD_INVALID_FORMAT', params: { expected_format: 'phone' } })
  ],
  [
    PhoneVerdict.INVALID_NUMBER,
    () => ({ code: 'FIELD_INVALID_FORMAT', params: { expected_format: 'phone' } })
  ]
])

const VERDICT_BY_CODE: ReadonlyMap<string, PhoneVerdict> = new Map([
  ['FIELD_REQUIRED', PhoneVerdict.EMPTY],
  ['FIELD_TOO_SHORT', PhoneVerdict.INCOMPLETE],
  ['FIELD_INVALID_FORMAT', PhoneVerdict.INVALID_NUMBER]
])

const PHONE_POINTER_SEGMENT = /(?:^|_)phone$/u

/** The verdict a server field issue stands for when it points at a phone field; undefined for any other field. */
function verdictOfIssue(issue: Pick<ApiFieldIssue, 'code' | 'source'>): PhoneVerdict | undefined {
  const field = issue.source.pointer.split('/').at(-1) ?? ''
  return PHONE_POINTER_SEGMENT.test(field) ? VERDICT_BY_CODE.get(issue.code) : undefined
}

const MESSAGE_KEY_BY_VERDICT: ReadonlyMap<PhoneVerdict, string> = new Map([
  [PhoneVerdict.EMPTY, PhoneMessageKey.required],
  [PhoneVerdict.INCOMPLETE, PhoneMessageKey.incomplete],
  [PhoneVerdict.INVALID_CHARACTERS, PhoneMessageKey.invalidCharacters],
  [PhoneVerdict.INVALID_NUMBER, PhoneMessageKey.invalid]
])

/** The typed field-error code and params that present a failing verdict through the field-error presenter. */
function fieldIssue(verdict: PhoneVerdict): Pick<ApiFieldIssue, 'code' | 'params'> {
  const build = FIELD_ISSUE_BY_VERDICT.get(verdict)
  if (build === undefined) throw new TypeError(`No field issue for the ${verdict} verdict`)
  return build()
}

/** The locale key of a failing verdict's message. */
function messageKey(verdict: PhoneVerdict): string {
  const key = MESSAGE_KEY_BY_VERDICT.get(verdict)
  if (key === undefined) throw new TypeError(`No message for the ${verdict} verdict`)
  return key
}

/** Every region libphonenumber knows, with its calling code, for the country picker list. */
function regions(): { code: string; callingCode: string }[] {
  return getCountries().map((code) => ({ callingCode: getCountryCallingCode(code), code }))
}

export const PhoneNumber = {
  defaultCountry: DEFAULT_COUNTRY,
  digitCount,
  fieldIssue,
  display,
  hasInvalidCharacters,
  mask,
  messageKey,
  parse,
  regions,
  ruleParams,
  ruleVerdict,
  verdictOfIssue,
  wire
}
